import { memo, useCallback, useEffect, useState } from 'react'
import { Spinner } from '../common/Spinner.js'
import {
  epgApi,
  EPG_STATUS_LABELS,
  EPG_TRIGGER_LABELS,
  type EpgConfig,
  type EpgConfigPatch,
  type EpgDayStatus,
  type EpgExportRecord,
} from '../../lib/settingsApi.js'

/**
 * Export de la grille vers l'EPG (Plurimedia / Media Press).
 *
 * Un fichier `TVAAAAMMJJ.xml` par journée d'antenne, déposé sur le FTP du
 * partenaire et redéposé chaque fois que la journée change. L'écran sert trois
 * usages d'exploitation qui n'ont pas la même fréquence :
 *
 * 1. **Le paramétrage**, fait une fois : FTP, fenêtre de publication, heure du
 *    dépôt quotidien.
 * 2. **Le contrôle quotidien** : la liste des journées dit laquelle a changé
 *    depuis son dernier dépôt, et permet de forcer celle qui presse.
 * 3. **La vérification d'un mapping** : la prévisualisation télécharge le flux
 *    exact qui partirait, sans rien déposer chez le partenaire.
 *
 * Le mot de passe FTP est en écriture seule : l'API ne renvoie qu'un booléen de
 * présence. Un champ laissé vide conserve donc le secret enregistré.
 */

type Busy = 'save' | 'test' | 'send-all' | 'preview' | string | null

const HORIZON_CHOICES = [
  { value: 7,  label: '7 jours (une semaine)' },
  { value: 14, label: '14 jours (deux semaines)' },
  { value: 21, label: '21 jours (trois semaines)' },
  { value: 28, label: '28 jours (quatre semaines)' },
  { value: 42, label: '42 jours (six semaines)' },
]

function formatBytes(bytes: number): string {
  return bytes < 1024 ? `${bytes} o` : `${(bytes / 1024).toFixed(1)} kio`
}

/** « 2026-08-23 » → « dim. 23 août », l'étiquette que l'exploitant reconnaît. */
function formatDayLabel(dayKey: string): string {
  return new Date(`${dayKey}T12:00:00`).toLocaleDateString('fr-FR', {
    weekday: 'short',
    day:     'numeric',
    month:   'short',
  })
}

export const EpgSettings = memo(function EpgSettings() {
  const [config, setConfig]   = useState<EpgConfig | null>(null)
  const [days, setDays]       = useState<EpgDayStatus[]>([])
  const [history, setHistory] = useState<EpgExportRecord[]>([])
  const [password, setPassword] = useState('')
  const [busy, setBusy]         = useState<Busy>(null)
  const [message, setMessage]   = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)

  /**
   * La fenêtre de publication reconstruit le flux de chaque journée pour la
   * comparer au dernier dépôt : c'est plusieurs requêtes de grille, donc un
   * chargement séparé du reste de la page, dont l'échec ne bloque pas le
   * paramétrage du FTP.
   */
  const loadDays = useCallback(async () => {
    try {
      const [{ days: rows }, { exports }] = await Promise.all([epgApi.days(), epgApi.exports(30)])
      setDays(rows)
      setHistory(exports)
    } catch { /* Secondaire : la configuration reste modifiable. */ }
  }, [])

  const load = useCallback(async () => {
    const { config: loaded } = await epgApi.get()
    setConfig(loaded)
    await loadDays()
  }, [loadDays])

  useEffect(() => {
    void load().catch(error => setMessage({ tone: 'error', text: error.message }))
  }, [load])

  const patch = useCallback((values: Partial<EpgConfig>) => {
    setConfig(current => current ? { ...current, ...values } : current)
  }, [])

  const fail = useCallback((error: unknown) => {
    setMessage({ tone: 'error', text: error instanceof Error ? error.message : String(error) })
  }, [])

  const handleSave = useCallback(async () => {
    if (!config) return
    setBusy('save'); setMessage(null)
    try {
      const body: EpgConfigPatch = {
        enabled:        config.enabled,
        filePrefix:     config.filePrefix,
        horizonDays:    config.horizonDays,
        dailyHour:      config.dailyHour,
        resendOnChange: config.resendOnChange,
        ftpHost:        config.ftpHost,
        ftpPort:        config.ftpPort,
        ftpSecure:      config.ftpSecure,
        ftpUser:        config.ftpUser,
        ftpDirectory:   config.ftpDirectory,
      }
      // Champ vide = mot de passe inchangé : sans cette règle, chaque
      // enregistrement d'un autre réglage effacerait l'identifiant FTP.
      if (password !== '') body.ftpPassword = password

      const { config: saved } = await epgApi.update(body)
      setConfig(saved)
      setPassword('')
      setMessage({ tone: 'ok', text: 'Configuration enregistrée' })
      await loadDays()
    } catch (error) { fail(error) } finally { setBusy(null) }
  }, [config, password, loadDays, fail])

  const handleTest = useCallback(async () => {
    setBusy('test'); setMessage(null)
    try {
      const result = await epgApi.test()
      setMessage({ tone: result.ok ? 'ok' : 'error', text: result.message })
    } catch (error) { fail(error) } finally { setBusy(null) }
  }, [fail])

  const handleSend = useCallback(async (day?: string) => {
    setBusy(day ?? 'send-all'); setMessage(null)
    try {
      // `force` sur un dépôt à la journée : l'exploitant qui clique sur une
      // journée précise veut la redéposer, même si le partenaire a déjà ce
      // contenu — le plus souvent parce qu'il doute de ce qui a été ingéré.
      const { outcomes } = await epgApi.send(day ? { day, force: true } : {})
      const sent    = outcomes.filter(outcome => outcome.status === 'SENT').length
      const failed  = outcomes.filter(outcome => outcome.status === 'FAILED')
      const skipped = outcomes.filter(outcome => outcome.status === 'SKIPPED').length
      setMessage(failed.length > 0
        ? { tone: 'error', text: `${failed.length} échec(s) : ${failed[0].error ?? 'cause inconnue'}` }
        : { tone: 'ok', text: `${sent} journée(s) transmise(s)${skipped > 0 ? `, ${skipped} inchangée(s)` : ''}` })
      await loadDays()
    } catch (error) { fail(error) } finally { setBusy(null) }
  }, [loadDays, fail])

  const handlePreview = useCallback(async (day: string, fileName: string) => {
    setBusy(`preview-${day}`); setMessage(null)
    try {
      const blob = await epgApi.preview(day)
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = fileName
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (error) { fail(error) } finally { setBusy(null) }
  }, [fail])

  if (!config) {
    return <div className="settings-content--wide"><p><Spinner /> Chargement de la configuration…</p></div>
  }

  const ftpIncomplete = !config.ftpHost || !config.ftpUser || (!config.hasFtpPassword && password === '')

  return (
    <div className="settings-content--wide">
      <div className="settings-view-header">
        <div>
          <h2 className="settings-view-title">Export EPG</h2>
          <p className="settings-view-description">
            Dépôt de la grille chez Plurimedia / Media Press : un fichier par journée d’antenne, redéposé
            à chaque modification de la programmation.
          </p>
        </div>
        <div className="settings-view-header__actions">
          {message && (
            <span className={message.tone === 'ok' ? 'settings-saved' : 'settings-error'} role="status">
              {message.tone === 'ok' ? '✓ ' : '⚠ '}{message.text}
            </span>
          )}
          <button className="tp-button tp-button--primary" disabled={busy !== null} onClick={() => void handleSave()}>
            Sauvegarder
          </button>
        </div>
      </div>

      {/* ── Publication ───────────────────────────────────────────────────── */}
      <section className="settings-section" aria-labelledby="epg-publish-title">
        <div className="settings-section__heading">
          <h3 id="epg-publish-title" className="settings-section__title">Publication</h3>
          <p className="settings-section__description">
            Ce que reçoit le partenaire : les émissions de la grille, avec leur titre, leur résumé et leur
            genre. Les écrans publicitaires, éléments d’antenne et bouche-trous n’y
            figurent pas.
          </p>
        </div>

        <label className="settings-option-row settings-option-row--selectable">
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={event => patch({ enabled: event.target.checked })}
          />
          <span className="settings-option-row__copy">
            <span className="settings-option-row__title">Activer les dépôts automatiques</span>
            <span className="settings-option-row__description">
              Coupé, la grille reste consultable et déposable à la main, mais rien ne part tout seul.
            </span>
          </span>
        </label>

        <label className="settings-option-row settings-option-row--selectable">
          <input
            type="checkbox"
            checked={config.resendOnChange}
            onChange={event => patch({ resendOnChange: event.target.checked })}
          />
          <span className="settings-option-row__copy">
            <span className="settings-option-row__title">Redéposer après une modification</span>
            <span className="settings-option-row__description">
              Vérifie la grille toutes les 5 minutes et ne redépose qu’une journée réellement modifiée.
              Sans cette option, une correction n’atteint le partenaire qu’au dépôt du lendemain.
            </span>
          </span>
        </label>

        <div className="settings-redundancy__form-grid">
          <label>
            <span>Fenêtre de publication</span>
            <select
              className="tp-input"
              value={config.horizonDays}
              onChange={event => patch({ horizonDays: Number(event.target.value) })}
            >
              {HORIZON_CHOICES.map(choice => (
                <option key={choice.value} value={choice.value}>{choice.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Heure du dépôt quotidien</span>
            <select
              className="tp-input"
              value={config.dailyHour}
              onChange={event => patch({ dailyHour: Number(event.target.value) })}
            >
              {Array.from({ length: 24 }, (_, hour) => (
                <option key={hour} value={hour}>{String(hour).padStart(2, '0')} h</option>
              ))}
            </select>
          </label>
          <label>
            <span>Préfixe du nom de fichier</span>
            <input
              className="tp-input"
              value={config.filePrefix}
              onChange={event => patch({ filePrefix: event.target.value })}
            />
          </label>
        </div>
        <p className="settings-section__description">
          Les fichiers s’appellent <code>{config.filePrefix}AAAAMMJJ.xml</code>. Le nom est lu par un
          automate côté partenaire : ne le changez qu’à sa demande. Les journées passées ne sont jamais
          redéposées — le partenaire a déjà publié sa grille.
        </p>
      </section>

      {/* ── FTP partenaire ────────────────────────────────────────────────── */}
      <section className="settings-section" aria-labelledby="epg-ftp-title">
        <div className="settings-section__heading">
          <h3 id="epg-ftp-title" className="settings-section__title">FTP du partenaire</h3>
          <p className="settings-section__description">
            Identifiants fournis par Plurimedia. Le fichier est écrit sous un nom temporaire puis renommé :
            le partenaire ne peut pas ingérer un dépôt à moitié transféré.
          </p>
        </div>

        <div className="settings-redundancy__form-grid">
          <label>
            <span>Hôte</span>
            <input
              className="tp-input"
              placeholder="ftp.plurimedia.fr"
              value={config.ftpHost ?? ''}
              onChange={event => patch({ ftpHost: event.target.value || null })}
            />
          </label>
          <label>
            <span>Port</span>
            <input
              className="tp-input" type="number" min={1} max={65535}
              value={config.ftpPort}
              onChange={event => patch({ ftpPort: Number(event.target.value) })}
            />
          </label>
          <label>
            <span>Utilisateur</span>
            <input
              className="tp-input"
              value={config.ftpUser ?? ''}
              onChange={event => patch({ ftpUser: event.target.value || null })}
            />
          </label>
          <label>
            <span>Mot de passe</span>
            <input
              className="tp-input" type="password"
              placeholder={config.hasFtpPassword ? '•••••••• (enregistré)' : 'Non renseigné'}
              value={password}
              onChange={event => setPassword(event.target.value)}
            />
          </label>
          <label>
            <span>Répertoire de dépôt</span>
            <input
              className="tp-input"
              placeholder="/"
              value={config.ftpDirectory}
              onChange={event => patch({ ftpDirectory: event.target.value })}
            />
          </label>
        </div>

        <label className="settings-option-row settings-option-row--selectable">
          <input
            type="checkbox"
            checked={config.ftpSecure}
            onChange={event => patch({ ftpSecure: event.target.checked })}
          />
          <span className="settings-option-row__copy">
            <span className="settings-option-row__title">Chiffrer la connexion (FTPS)</span>
            <span className="settings-option-row__description">
              À activer si le partenaire l’accepte : sans chiffrement, l’identifiant circule en clair.
            </span>
          </span>
        </label>

        <p className="settings-section__description">
          Le mot de passe est chiffré en base et n’est jamais renvoyé par l’interface. Laissez le champ vide
          pour conserver celui déjà enregistré.
        </p>

        <div className="settings-actions">
          <button className="tp-button" disabled={busy !== null || ftpIncomplete} onClick={() => void handleTest()}>
            {busy === 'test' ? 'Connexion…' : 'Tester la connexion'}
          </button>
          <button
            className="tp-button"
            disabled={busy !== null || ftpIncomplete}
            onClick={() => void handleSend()}
          >
            {busy === 'send-all' ? 'Dépôt en cours…' : 'Déposer toute la fenêtre'}
          </button>
        </div>
      </section>

      {/* ── Fenêtre de publication ────────────────────────────────────────── */}
      <section className="settings-section" aria-labelledby="epg-days-title">
        <div className="settings-section__heading">
          <h3 id="epg-days-title" className="settings-section__title">Journées à publier</h3>
          <p className="settings-section__description">
            « À redéposer » signale une journée modifiée depuis son dernier dépôt réussi. « Télécharger »
            récupère le fichier exact qui partirait, sans rien envoyer au partenaire.
          </p>
        </div>

        {days.length === 0
          ? <p className="settings-section__description">Aucune journée à publier — vérifiez qu’une chaîne est active.</p>
          : (
            <ol className="settings-epg-days">
              {days.map(day => (
                <li key={day.dayKey} className={day.lastStatus === 'FAILED' ? 'is-failed' : undefined}>
                  <time dateTime={day.dayKey}>{formatDayLabel(day.dayKey)}</time>
                  <span>{day.programCount} programme{day.programCount > 1 ? 's' : ''}</span>
                  <span>
                    {day.lastSentAt
                      ? `Déposé le ${new Date(day.lastSentAt).toLocaleString('fr-FR')}`
                      : 'Jamais déposé'}
                  </span>
                  <span>
                    {day.lastStatus === 'FAILED'
                      ? `⚠ Échec : ${day.lastError ?? 'cause inconnue'}`
                      : day.pendingChange ? '● À redéposer' : '✓ À jour'}
                  </span>
                  <span className="settings-epg-days__actions">
                    <button
                      className="tp-button"
                      disabled={busy !== null}
                      onClick={() => void handlePreview(day.dayKey, day.fileName)}
                    >
                      {busy === `preview-${day.dayKey}` ? '…' : 'Télécharger'}
                    </button>
                    <button
                      className="tp-button"
                      disabled={busy !== null || ftpIncomplete}
                      onClick={() => void handleSend(day.dayKey)}
                    >
                      {busy === day.dayKey ? '…' : 'Déposer'}
                    </button>
                  </span>
                </li>
              ))}
            </ol>
          )}
      </section>

      {/* ── Historique ────────────────────────────────────────────────────── */}
      <section className="settings-section" aria-labelledby="epg-history-title">
        <div className="settings-section__heading">
          <h3 id="epg-history-title" className="settings-section__title">Derniers dépôts</h3>
          <p className="settings-section__description">
            Trace de ce qui a été transmis, et la cause des échecs. « Inchangé » est un fonctionnement
            normal : la journée était identique au dépôt précédent.
          </p>
        </div>
        {history.length === 0
          ? <p className="settings-section__description">Aucun dépôt enregistré.</p>
          : (
            <ol className="settings-notification-history">
              {history.map(record => (
                <li key={record.id}>
                  <time>{new Date(record.createdAt).toLocaleString('fr-FR')}</time>
                  <span>{record.fileName} · {record.programCount} prog. · {formatBytes(record.bytes)}</span>
                  <span>{EPG_TRIGGER_LABELS[record.trigger] ?? record.trigger}</span>
                  <span>
                    {record.status === 'FAILED'
                      ? `⚠ Échec : ${record.error ?? 'cause inconnue'}`
                      : EPG_STATUS_LABELS[record.status] ?? record.status}
                  </span>
                </li>
              ))}
            </ol>
          )}
      </section>
    </div>
  )
})
